import React from 'react';
import { FastField as BaseFormikFastField, Form, Formik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { Button, Fields } from '@ets-global/b2c-website-ui';

import { Title, Tooltip } from '../../../../../components/Base';
import { NumberByLocale } from '../../../../../components/Element';
import CandidatePictureUpload from '../../../../../components/CandidatePictureUpload';
import CandidateCorrectionRadioField from './CandidateCorrectionRadioField';
import { translateProductNameCallback } from '../../../../../helpers/i18n';
import { useCandidateValidator } from '../../useCandidateValidator';

const CandidateFormikForm = ({
  onSubmit,
  className,
  candidate: candidateInitial = {},
  order,
  sessionId,
  testType,
  productsAndServices: {
    correctionProductsAndServices,
    certificateProductsAndServices,
    discountProductsAndServices,
    includedProductsAndServices,
  },
  withPicture = false,
  withDateOfBirth = false,
  notEditable = false,
  loading = false,
}) => {
  const { i18n, t } = useTranslation();
  const translateProductName = translateProductNameCallback(i18n, t);
  const { validationSchema } = useCandidateValidator(withDateOfBirth);
  const defaultDate = new Date();
  defaultDate.setFullYear(1999);
  const isSingleProductAndService = !correctionProductsAndServices.length;

  //candidate reference clone
  const candidate = Object.assign({}, candidateInitial);

  // Format candidates P&S for form
  if (candidate && candidate.productsAndServices) {
    correctionProductsAndServices.forEach((ps) => {
      for (let candidatePS of candidate.productsAndServices) {
        if (parseInt(candidatePS.id, 10) === parseInt(ps.id, 10)) {
          candidate.correction = ps.id;

          return;
        }
      }
    });

    discountProductsAndServices.forEach((ps) => {
      for (let candidatePS of candidate.productsAndServices) {
        if (parseInt(candidatePS.id, 10) === parseInt(ps.id, 10)) {
          candidate.discount = ps.id;

          return;
        }
      }
    });

    candidate.certificates = {};

    certificateProductsAndServices.forEach((ps) => {
      for (let candidatePS of candidate.productsAndServices) {
        if (parseInt(candidatePS.id, 10) === parseInt(ps.id, 10)) {
          candidate.certificates[ps.id] = candidatePS.quantity;
        }
      }
    });
  }

  const transformCandidate = ({ certificates, discount, correction, ...candidate }, actions) => {
    candidate.productsAndServices = [];

    if (discount !== 'none') {
      candidate.productsAndServices.push({ id: discount, quantity: 1 });
    }

    if (correction !== 'none') {
      candidate.productsAndServices.push({ id: correction, quantity: 1 });
    }

    Object.entries(certificates).forEach((certificate) => {
      let quantity = parseInt(certificate[1]);
      if (quantity > 0) {
        candidate.productsAndServices.push({ id: certificate[0], quantity });
      }
    });

    onSubmit({ candidate }, actions);
  };

  let hasCorrectionProductAndServiceInDiscount = false;
  correctionProductsAndServices.forEach((product) => {
    if (product.isMandatory) {
      hasCorrectionProductAndServiceInDiscount = true;
      candidate.correction = product.id;
    }
  });

  let hasDiscountProductAndServiceInDiscount = false;
  discountProductsAndServices.forEach((product) => {
    if (product.isMandatory) {
      hasDiscountProductAndServiceInDiscount = true;
      candidate.discount = product.id;
    }
  });

  certificateProductsAndServices.forEach((product) => {
    if (product.isMandatory) {
      if (!candidate.certificates) {
        candidate.certificates = {};
      }
      candidate.certificates[product.id] = 1;
    }
  });

  return (
    <Formik
      initialValues={{
        ...Object.keys(candidate).reduce(
          (initialValues, key) => {
            let value = candidate[key];
            if (value) {
              initialValues[key] = value;
            }

            return initialValues;
          },
          {
            firstname: '',
            lastname: '',
            email: '',
            dateOfBirth: null,
            phoneNumber: '',
            correction: 'none',
            discount: 'none',
            pictureId: null,
            pictureUrl: null,
            certificates: {},
          },
        ),
      }}
      validationSchema={validationSchema}
      onSubmit={transformCandidate}
      enableReinitialize={true}
      render={({ values, setFieldValue, isSubmitting }) => {
        return (
          <Form>
            <div className="candidate-infos">
              <div className="candidate-infos__heading">
                <div>
                  <Title.SmallTitle>
                    <Trans values={{ candidateNumber: order }}>session-registration.candidate.current-count</Trans>
                  </Title.SmallTitle>
                  <p>
                    <Trans>session-registration.candidate.fill-required-info</Trans>
                  </p>
                </div>
                <Button
                  type={'submit'}
                  loading={loading}
                  disabled={isSubmitting}
                  data-cypress="buying-process.validate-candidate"
                >
                  <Trans>session-registration.candidate.validate</Trans>
                </Button>
              </div>
              <p className="candidate-infos__mentions">
                <sup>*</sup>
                <Trans>session-registration.candidate.required-fields</Trans>
              </p>
              <div className="form__row">
                <Fields.FormikFastField
                  component={Fields.TextField}
                  id="firstname"
                  name="firstname"
                  className={classNames('field--dotted', className)}
                  label={t('registration.form.label.firstname')}
                  autoComplete={'given-name'}
                  required={true}
                  disabled={Boolean(notEditable && candidate?.firstname)}
                  onBlur={(e) => setFieldValue('firstname', e.target.value.trim())}
                />
                <Fields.FormikFastField
                  component={Fields.TextField}
                  id="lastname"
                  name="lastname"
                  className={classNames('field--dotted', className)}
                  label={t('registration.form.label.lastname')}
                  autoComplete={'family-name'}
                  required={true}
                  disabled={Boolean(notEditable && candidate?.lastname)}
                  onBlur={(e) => setFieldValue('lastname', e.target.value.trim())}
                />
                <Fields.FormikFastField
                  component={Fields.TextField}
                  id="email"
                  name="email"
                  className={classNames('field--dotted', className)}
                  label={t('registration.form.label.email')}
                  autoComplete={'email'}
                  required={true}
                  disabled={Boolean(notEditable && candidate?.email)}
                  onBlur={(e) => setFieldValue('email', e.target.value.trim())}
                />
                {withDateOfBirth && (
                  <Fields.FormikFastField
                    icon="calendar"
                    component={Fields.DatePickerField}
                    id="dateOfBirth"
                    name="dateOfBirth"
                    className={classNames('field--dotted', className)}
                    label={t('registration.form.label.dateOfBirth')}
                    required={true}
                    disabled={Boolean(notEditable && candidate?.dateOfBirth)}
                    maxDate={'today'}
                    setFieldValue={(value) => setFieldValue('dateOfBirth', value)}
                    calendarDefaultDate={defaultDate}
                  />
                )}
                <BaseFormikFastField
                  component={Fields.PhoneNumberField}
                  id="phoneNumber"
                  name="phoneNumber"
                  className={classNames('field--dotted', className)}
                  labels={[t('registration.form.label.country'), t('registration.form.label.phonenumber')]}
                  required={false}
                />
                {withPicture && (
                  <div className="candidate-infos__upload-photo">
                    <CandidatePictureUpload
                      sessionId={sessionId}
                      candidate={values}
                      withPicture={withPicture}
                      onFinished={(updatedCandidate) => {
                        setFieldValue('pictureId', updatedCandidate.pictureId);
                        setFieldValue('pictureUrl', updatedCandidate.pictureUrl);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="candidate-options">
              <div className="candidate-options__sections">
                <div className="candidate-options__section">
                  <h3 className="candidate-options__section-title">
                    <Trans>session-registration.products-and-services.certificates.title</Trans>
                  </h3>
                  <div className="candidate-options__certificates">
                    {includedProductsAndServices &&
                      includedProductsAndServices.map((includedPS, key) => {
                        return (
                          <div key={key} className="candidate-options__certificate">
                            <div className="candidate-options__certificate-label">
                              <label htmlFor="attestation-results">
                                {translateProductName(includedPS.translationKey)}
                              </label>
                            </div>
                            <Tooltip position={'top-left'} icon={'info'} width={'400px'}>
                              <Trans>{includedPS.translationKey}.tooltip</Trans>
                            </Tooltip>
                            <div className="field-wrapper">
                              <input type="text" value="1" className="input has-value input--dotted" disabled />
                            </div>
                            <span className="candidate-options__certificate-price">
                              <Trans>session-registration.products-and-services.certificateInclude.free</Trans>
                            </span>
                          </div>
                        );
                      })}
                    {certificateProductsAndServices &&
                      certificateProductsAndServices.map((certificate, key) => {
                        return (
                          <div key={key} className="candidate-options__certificate">
                            <div className="candidate-options__certificate-label">
                              <label htmlFor="attestation-results">
                                {translateProductName(certificate.translationKey)}
                              </label>
                            </div>
                            <Tooltip position={'top-left'} icon={'info'} width={'400px'}>
                              <Trans>{certificate.translationKey}.tooltip</Trans>
                            </Tooltip>
                            <Fields.FormikFastField
                              id={`certificates.${certificate.id}`}
                              name={`certificates.${certificate.id}`}
                              isMandatory={certificate.isMandatory}
                              component={Fields.SelectOrNumberField}
                              setFieldValue={(value) => setFieldValue(`certificates.${certificate.id}`, value)}
                              disabled={notEditable}
                            />
                            <span className="candidate-options__certificate-price">
                              <NumberByLocale currency={certificate.price.currency} value={certificate.price.value} />
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>

                <div className="candidate-options__section">
                  <h3 className="candidate-options__section-title">
                    <Trans>session-registration.products-and-services.discounts.title</Trans>
                  </h3>
                  <Fields.RadioButtonGroup value={values.discount} className={'candidate-options__discounts'}>
                    <Fields.FormikFastField
                      component={Fields.RadioField}
                      name={'discount'}
                      disabled={hasDiscountProductAndServiceInDiscount}
                      id={'none'}
                      label={<Trans>session-registration.products-and-services.discount.none</Trans>}
                    />
                    {discountProductsAndServices &&
                      discountProductsAndServices.map((discount, key) => {
                        return (
                          <div key={key} className="candidate-options__discount">
                            <Fields.FormikFastField
                              component={Fields.RadioField}
                              name={'discount'}
                              disabled={notEditable || hasDiscountProductAndServiceInDiscount}
                              id={discount.id}
                              label={translateProductName(discount.translationKey)}
                            />

                            <Tooltip position={'top-left'} icon={'info'} width={'400px'}>
                              <Trans>{discount.translationKey}.tooltip</Trans>
                            </Tooltip>
                            <span className="candidate-options__certificate-price">
                              <NumberByLocale currency={discount.price.currency} value={discount.price.value} />
                            </span>
                          </div>
                        );
                      })}
                  </Fields.RadioButtonGroup>
                </div>
              </div>
              <div className="candidate-options__section">
                {isSingleProductAndService && (
                  <h3 className="candidate-options__section-title">
                    <Trans>session-registration.products-and-services.shipping-information.title</Trans>
                  </h3>
                )}
                {!isSingleProductAndService && (
                  <div>
                    <h3 className="candidate-options__section-title">
                      <Trans>session-registration.products-and-services.correction-options.title</Trans>
                    </h3>
                    <p className="candidate-options__section-subtitle">
                      <Trans>session-registration.products-and-services.correction-options.description</Trans>
                    </p>
                  </div>
                )}
                <Fields.RadioButtonGroup value={values.correction} className={'candidate-options__corrections'}>
                  <BaseFormikFastField
                    component={CandidateCorrectionRadioField}
                    name={'correction'}
                    disabled={hasCorrectionProductAndServiceInDiscount}
                    testType={testType}
                    isSingle={isSingleProductAndService}
                  />
                  {correctionProductsAndServices &&
                    correctionProductsAndServices.map((correction, key) => {
                      return (
                        <BaseFormikFastField
                          key={key}
                          component={CandidateCorrectionRadioField}
                          disabled={hasCorrectionProductAndServiceInDiscount}
                          name={'correction'}
                          correction={correction}
                        />
                      );
                    })}
                </Fields.RadioButtonGroup>
              </div>
            </div>
            <Button className="button--full-width" type={'submit'} loading={loading} disabled={isSubmitting}>
              <Trans>session-registration.candidate.validate</Trans>
            </Button>
          </Form>
        );
      }}
    />
  );
};

CandidateFormikForm.propTypes = {
  candidate: PropTypes.shape({
    identificationId: PropTypes.string,
    token: PropTypes.string,
    firstname: PropTypes.string.isRequired,
    lastname: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    dateOfBirth: PropTypes.string,
    phoneNumber: PropTypes.string,
    pictureId: PropTypes.number,
    pictureUrl: PropTypes.string,
  }),
  className: PropTypes.string,
  formikRef: PropTypes.object,
  onSubmit: PropTypes.func,
  order: PropTypes.number,
  notEditable: PropTypes.bool,
  productsAndServices: PropTypes.shape({
    correctionProductsAndServices: PropTypes.array,
    certificateProductsAndServices: PropTypes.array,
    discountProductsAndServices: PropTypes.array,
  }),
  sessionId: PropTypes.number.isRequired,
  testType: PropTypes.shape({
    translationKey: PropTypes.string,
  }),
  withPicture: PropTypes.bool,
  withDateOfBirth: PropTypes.bool,
};

export default CandidateFormikForm;
